import React, { useRef } from 'react';
import { cn } from '@/lib/utils';
import { FOCUS_ACCENT } from './hooks';
import { arcPath, lengthAt, minuteDeg, minuteOf, polar } from './clockGeometry';

const HOUR_MS = 60 * 60 * 1000;

interface ClockDialProps {
  size: number;
  /** The time the hands show (re-render each second to tick). */
  now: number;
  /** Focus done so far, ms: a marker-yellow arc that ends at the minute hand. */
  doneMs?: number;
  /** Planned time still to come, ms: a fainter arc running on from the minute hand. */
  aheadMs?: number;
  /** A break: the arcs draw in ink instead of yellow. */
  muted?: boolean;
  /** Time's up: the done arc pulses. */
  ringing?: boolean;
  /** Drag round the dial to set a length in whole minutes (1 to 60), measured from the minute hand. */
  onSetMinutes?: (minutes: number) => void;
  className?: string;
}

/**
 * The focus timer as a wall clock, after the time-planner look: a soft face
 * with its numerals, two rings of dots, the hands showing the time now, and
 * the session marked on the dial in highlighter yellow. The yellow you have
 * done ends at the minute hand and grows as you work; what is still planned
 * runs on ahead of the hand, fainter. An hour of focus fills the ring.
 */
export function ClockDial({ size, now, doneMs = 0, aheadMs = 0, muted, ringing, onSetMinutes, className }: ClockDialProps) {
  const c = size / 2;
  const dragRef = useRef<number | null>(null);

  const outerDots = c - 5;
  const innerDots = c - 17;
  const face = c - 27;
  const band = c - 27;
  const bandWidth = Math.max(8, size * 0.05);
  const numerals = face - size * 0.085;

  const hand = minuteOf(now);
  const handDeg = minuteDeg(hand);
  const doneSweep = Math.min(360, (Math.max(0, doneMs) / HOUR_MS) * 360);
  const aheadSweep = Math.min(360 - doneSweep, (Math.max(0, aheadMs) / HOUR_MS) * 360);

  const d = new Date(now);
  const hourDeg = ((d.getHours() % 12) + d.getMinutes() / 60) * 30;
  const secondDeg = d.getSeconds() * 6;
  const arcColor = muted ? 'hsl(var(--foreground) / 0.18)' : 'hsl(var(--highlight))';

  const pointerMinute = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    const deg = ((Math.atan2(x, -y) * 180) / Math.PI + 360) % 360;
    return (deg / 360) * 60;
  };
  const pointer = onSetMinutes
    ? {
        onPointerDown: (e: React.PointerEvent<SVGSVGElement>) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          const prev = Math.min(60, Math.max(1, Math.round(aheadMs / 60000)));
          const m = lengthAt(pointerMinute(e), hand, prev);
          dragRef.current = m;
          onSetMinutes(m);
        },
        onPointerMove: (e: React.PointerEvent<SVGSVGElement>) => {
          if (dragRef.current === null) return;
          const m = lengthAt(pointerMinute(e), hand, dragRef.current);
          if (m !== dragRef.current) {
            dragRef.current = m;
            onSetMinutes(m);
          }
        },
        onPointerUp: () => {
          dragRef.current = null;
        },
        onPointerCancel: () => {
          dragRef.current = null;
        },
      }
    : {};

  const handLine = (deg: number, length: number, tail = 0) => {
    const [x1, y1] = polar(c, -tail, deg);
    const [x2, y2] = polar(c, length, deg);
    return { x1, y1, x2, y2 };
  };

  return (
    // select-none: a drag round the dial must not select the numerals
    <div className={cn('relative shrink-0 select-none', className)} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={cn('block', onSetMinutes && 'cursor-grab touch-none active:cursor-grabbing')}
        aria-hidden
        {...pointer}
      >
        {/* Two rings of dots: every fifth on the outer ring is larger */}
        {Array.from({ length: 60 }, (_, i) => {
          const [x, y] = polar(c, outerDots, i * 6);
          const major = i % 5 === 0;
          return <circle key={`o${i}`} cx={x} cy={y} r={major ? size * 0.0085 : size * 0.0045} className={major ? 'fill-foreground/70' : 'fill-foreground/35'} />;
        })}
        {Array.from({ length: 60 }, (_, i) => {
          const [x, y] = polar(c, innerDots, i * 6);
          return <circle key={`i${i}`} cx={x} cy={y} r={size * 0.0035} className="fill-foreground/25" />;
        })}

        {/* The face, with fine minute ticks at its edge */}
        <circle cx={c} cy={c} r={face} className="fill-foreground/[0.035]" />
        {Array.from({ length: 60 }, (_, i) => {
          const major = i % 5 === 0;
          const [x1, y1] = polar(c, face - 2, i * 6);
          const [x2, y2] = polar(c, face - (major ? 7 : 4), i * 6);
          return <line key={`t${i}`} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth={major ? 1.2 : 0.8} strokeLinecap="round" className="stroke-foreground/25" />;
        })}

        {/* The session in highlighter yellow: still to come (faint), then done (full), ending at the minute hand */}
        {aheadSweep > 0 && (
          <path d={arcPath(c, band, handDeg, aheadSweep)} fill="none" stroke={arcColor} strokeWidth={bandWidth} strokeLinecap="round" opacity={0.4} />
        )}
        {doneSweep > 0 && (
          <path
            d={arcPath(c, band, handDeg - doneSweep, doneSweep)}
            fill="none"
            stroke={arcColor}
            strokeWidth={bandWidth}
            strokeLinecap="round"
            className={cn(ringing && 'animate-pulse motion-reduce:animate-none')}
          />
        )}

        {/* Numerals */}
        {Array.from({ length: 12 }, (_, i) => {
          const n = i + 1;
          const [x, y] = polar(c, numerals, n * 30);
          return (
            <text key={n} x={x} y={y} textAnchor="middle" dominantBaseline="central" className="fill-foreground" style={{ fontSize: size * 0.064, fontWeight: 450 }}>
              {n}
            </text>
          );
        })}

        {/* Hands: hour, minute, then the red second hand over them */}
        <line {...handLine(hourDeg, face * 0.5, face * 0.06)} strokeWidth={size * 0.022} strokeLinecap="round" className="stroke-foreground" />
        <line {...handLine(handDeg, face * 0.8, face * 0.06)} strokeWidth={size * 0.015} strokeLinecap="round" className="stroke-foreground" />
        <line {...handLine(secondDeg, face * 0.88, face * 0.16)} strokeWidth={1.2} strokeLinecap="round" stroke={FOCUS_ACCENT} />
        <circle cx={c} cy={c} r={size * 0.022} fill={FOCUS_ACCENT} />
        <circle cx={c} cy={c} r={size * 0.008} className="fill-background" />
      </svg>
    </div>
  );
}
